import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  StoryStructure,
  GeneratedOutline,
  VolumeOutline,
  DetailedChapterOutline,
  OpeningContent,
  Genre,
  WritingStyle,
} from '../../../shared/types';

interface AppState {
  // 分析结果
  analyzedStructure: StoryStructure | null;
  analysisId: string | null;

  // 生成设置
  genre: Genre | null;
  style: WritingStyle;
  targetWordCount: number;
  customPrompt: string;

  // 生成结果 (4 层)
  generatedOutline: GeneratedOutline | null;           // L1
  volumeOutline: VolumeOutline | null;                 // L2
  chapterOutline: DetailedChapterOutline | null;       // L3
  openingContent: OpeningContent | null;               // L4
  outlineVariations: GeneratedOutline[];

  // 已生成的层级
  generatedLevels: ('L1' | 'L2' | 'L3' | 'L4')[];

  // 演示模式
  isDemoMode: boolean;

  // 状态
  isAnalyzing: boolean;
  isGenerating: boolean;
  error: string | null;

  // Actions
  setAnalyzedStructure: (structure: StoryStructure, id: string) => void;
  setGenre: (genre: Genre) => void;
  setStyle: (style: WritingStyle) => void;
  setTargetWordCount: (count: number) => void;
  setCustomPrompt: (prompt: string) => void;
  setGeneratedOutline: (outline: GeneratedOutline, variations?: GeneratedOutline[], demoMode?: boolean) => void;
  setVolumeOutline: (volume: VolumeOutline) => void;
  setChapterOutline: (chapter: DetailedChapterOutline) => void;
  setOpeningContent: (opening: OpeningContent) => void;
  setGeneratedLevels: (levels: ('L1' | 'L2' | 'L3' | 'L4')[]) => void;
  setIsDemoMode: (isDemo: boolean) => void;
  setIsAnalyzing: (isAnalyzing: boolean) => void;
  setIsGenerating: (isGenerating: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

const initialState = {
  analyzedStructure: null,
  analysisId: null,
  genre: null,
  style: 'commercial' as WritingStyle,
  targetWordCount: 500000, // 默认 50 万字
  customPrompt: '',
  generatedOutline: null,
  volumeOutline: null,
  chapterOutline: null,
  openingContent: null,
  outlineVariations: [],
  generatedLevels: [] as ('L1' | 'L2' | 'L3' | 'L4')[],
  isDemoMode: false,
  isAnalyzing: false,
  isGenerating: false,
  error: null,
};

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      ...initialState,

      setAnalyzedStructure: (structure, id) =>
        set({ analyzedStructure: structure, analysisId: id }),

      setGenre: (genre) => set({ genre }),

      setStyle: (style) => set({ style }),

      setTargetWordCount: (count) => set({ targetWordCount: count }),

      setCustomPrompt: (customPrompt) => set({ customPrompt }),

      setGeneratedOutline: (outline, variations = [], demoMode = false) =>
        set({
          generatedOutline: outline,
          outlineVariations: variations,
          generatedLevels: ['L1' as const],
          isDemoMode: demoMode,
        }),

      setVolumeOutline: (volume) =>
        set((state) => ({
          volumeOutline: volume,
          generatedLevels: state.generatedLevels.includes('L2')
            ? state.generatedLevels
            : [...state.generatedLevels, 'L2' as const],
        })),

      setChapterOutline: (chapter) =>
        set((state) => ({
          chapterOutline: chapter,
          generatedLevels: state.generatedLevels.includes('L3')
            ? state.generatedLevels
            : [...state.generatedLevels, 'L3' as const],
        })),

      setOpeningContent: (opening) =>
        set((state) => ({
          openingContent: opening,
          generatedLevels: state.generatedLevels.includes('L4')
            ? state.generatedLevels
            : [...state.generatedLevels, 'L4' as const],
        })),

      setGeneratedLevels: (levels) => set({ generatedLevels: levels }),

      setIsDemoMode: (isDemoMode) => set({ isDemoMode }),

      setIsAnalyzing: (isAnalyzing) => set({ isAnalyzing }),

      setIsGenerating: (isGenerating) => set({ isGenerating }),

      setError: (error) => set({ error }),

      reset: () => set(initialState),
    }),
    {
      name: 'novel-outline-storage',
      partialize: (state) => ({
        analysisId: state.analysisId,
        genre: state.genre,
        style: state.style,
        targetWordCount: state.targetWordCount,
      }),
    }
  )
);
